import { type ReactNode, useState } from "react";
import { InfoIcon } from "lucide-react";

import { Column } from "@/components/Column";
import { Text } from "@/components/Text";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { cn } from "@/lib/utils";

/* ─── Section block (shared styling) ─── */

const SectionBlock = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Column className="gap-1.5">
    <Text variant="small">{label}</Text>
    <Column className="max-h-[25vh] overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
      {children}
    </Column>
  </Column>
);

/* ─── Sub-components ─── */

const Purpose = ({ children }: { children: ReactNode }) => (
  <SectionBlock label="Purpose">{children}</SectionBlock>
);

const Tips = ({ children }: { children: ReactNode }) => (
  <SectionBlock label="Helpful Tips">{children}</SectionBlock>
);

const Examples = ({ children }: { children: ReactNode }) => (
  <SectionBlock label="Examples">{children}</SectionBlock>
);

/* ─── Root component ─── */

const VoiceAiQuestionsInfoGuideRoot = ({
  children,
  className,
  title,
}: {
  children: ReactNode;
  className?: string;
  title: string;
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <InfoIcon
              className={cn(
                "h-3.5 w-3.5 shrink-0 cursor-pointer text-slate-400",
                className,
              )}
              onClick={(e) => {
                e.preventDefault();
                setModalOpen(true);
              }}
            />
          </TooltipTrigger>
          <TooltipContent
            side="top"
            className="max-h-80 max-w-lg overflow-y-auto p-3"
          >
            <Column className="gap-3">{children}</Column>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <Column className="gap-3">{children}</Column>
        </DialogContent>
      </Dialog>
    </>
  );
};

/* ─── Compound export ─── */

export const VoiceAiQuestionsInfoGuide = Object.assign(
  VoiceAiQuestionsInfoGuideRoot,
  {
    Purpose,
    Tips,
    Examples,
  },
);
